<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <v-card class="pt-1">
        <v-container fluid class="pa-0">
            <v-row>

                <!-- STATS -->
                <v-col cols="12" class="pb-0">
                    <v-card flat class="pr-4">
                        <v-container fluid class="pa-0">
                            <v-row dense>

                                <v-col cols="4" md="6" class="text-left pl-4">
                                    <help-icon class="mt-8" help="edit-panelist-activity"></help-icon>
                                </v-col>

                                <v-col cols="4" md="3">
                                    <stat-box-text
                                            v-show="lastActivity"
                                            :value="toHumanFrom(lastActivity)"
                                            hint="Last activity"
                                    ></stat-box-text>
                                </v-col>
                                <v-col cols="4" md="3">
                                    <stat-box-numerical
                                            :value="activityCount"
                                            hint="Number of activities"
                                    ></stat-box-numerical>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card>
                </v-col>

                <!-- ACTIVITY TABLE -->
                <v-col cols="12">

                    <v-data-table
                            :headers="headers"
                            :items="activity"
                            :options.sync="tableSettings"
                            :footer-props="{itemsPerPageOptions: [10,25,100, -1]}"
                            must-sort
                    >

                        <template v-slot:body="{ items }">
                            <tbody>

                            <tr v-if="items.length===0">
                                <td colspan="100">
                                    <v-alert color="info" icon="mdi-information" class="mt-4 body-2">
                                        No activity found
                                    </v-alert>
                                </td>
                            </tr>

                            <tr v-for="(item,i) in items" :key="i">

                                <td class="text-left" style="width: 1px">
                                    <v-chip>{{item.id}}</v-chip>
                                </td>

                                <!-- TYPE -->
                                <td>{{ item.type }}</td>

                                <!-- NAME -->
                                <td>{{ item.name }}</td>

                                <!-- STATUS -->
                                <td>
                                    <v-chip :color="getChipColor(item.status)">
                                        {{ item.status.toUpperCase() }}
                                    </v-chip>
                                </td>

                                <!-- FLAGS -->
                                <td>
                                    <div v-if="item.flags">
                                        <v-chip color="red" small label v-for="(flag, index) in item.flags" :key="index">
                                            {{flag}}
                                        </v-chip>
                                    </div>
                                </td>

                                <!-- REWARD -->
                                <td v-if="item.reward">{{ item.reward.toLocaleString() }}</td>
                                <td v-else><v-chip>N/A</v-chip></td>

                                <!-- REVENUE -->
                                <td v-if="item.revenue">{{ item.revenue.toLocaleString() }}</td>
                                <td v-else><v-chip>N/A</v-chip></td>

                                <!-- COST -->
                                <td v-if="item.cost">{{ item.cost.toLocaleString() }}</td>
                                <td v-else><v-chip>N/A</v-chip></td>

                                <td>{{ toLocalDateTime(item.created_at) }}</td>
                            </tr>
                            </tbody>
                        </template>

                    </v-data-table>

                </v-col>

                <!-- DEBUG -->
                <v-col cols="12" v-if="debug">
                    <v-card color="blue-grey darken-4">
                        <v-card-title>
                            <v-icon large class="mr-2">mdi-bug</v-icon>
                            <h3>Debug</h3>
                        </v-card-title>
                        <v-card-text>
                            <json :data="items"></json>
                            <json :data="activity"></json>
                        </v-card-text>
                    </v-card>
                </v-col>

            </v-row>
        </v-container>
    </v-card>
</template>

<script>
    import {timeDisplay} from '../../../../components/mixins/timeDisplayMixin';
    import Json from 'vue-json-pretty';
    import {mapState, mapMutations} from 'vuex';
    import StatBoxText from "../../../../components/UI/StatBoxText";
    import StatBoxNumerical from "../../../../components/UI/StatBoxNumerical";
    import moment from 'moment/moment';
    import HelpIcon from "../../../../components/UI/HelpIcon";
    import {statusColorMixin} from "../../../../components/mixins/statusColorMixin";

    export default {

        mixins: [timeDisplay,statusColorMixin],

        components: {
            Json,
            StatBoxText,
            StatBoxNumerical,
            HelpIcon
        },

        props: {
            items: {
                type: Array | null,
                required: true,
            }
        },

        data() {
            return {
                debug: false,
                activity: [],
                headers: [
                    {text: 'ID', sortable: true, value: 'id'},
                    {text: 'TYPE', sortable: true, value: 'type'},
                    {text: 'NAME', value: 'name'},
                    {text: 'STATUS', value: 'status'},
                    {text: 'FLAGS', sortable: false, value: 'flags'},
                    {text: 'REWARD', value: 'reward'},
                    {text: 'REVENUE', value: 'revenue'},
                    {text: 'COST', value: 'cost'},
                    {text: 'CREATED', value: 'created_at'},
                ],
            }
        },

        watch: {
            items(newItems) {
                if (newItems !== null) {
                    this.activity = JSON.parse(JSON.stringify(newItems));
                }
            }
        },

        mounted() {
            if (this.items !== null) {
                this.activity = JSON.parse(JSON.stringify(this.items));
            }
        },

        computed: {
            ...mapState('PanelistManager', [
                'activityTableSettings'
            ]),

            activityCount() {
                if (!this.activity || !this.activity.length) return 0;
                return this.activity.length;
            },

            lastActivity() {
                if (!this.activity || this.activity.length === 0) return null;

                let sortedActivity = JSON.parse(JSON.stringify(this.activity));

                // Re-sort by time stamp
                sortedActivity.sort((a, b) => {
                    return moment(a.created_at).unix() < moment(b.created_at).unix() ? -1 : 1;
                });

                return sortedActivity.pop().created_at;
            },

            tableSettings: {
                get() {
                    return this.activityTableSettings;
                },
                set(settings) {
                    this.setActivityTableSettings(settings);
                }
            }
        },

        methods: {

            ...mapMutations('PanelistManager', [
                'setActivityTableSettings'
            ]),

            getChipColor(status) {
                if (this.statusColors[status]) {
                    return this.statusColors[status];
                }
                return this.statusColors.started;
            },
        }
    }
</script>
